import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {MatSnackBar} from "@angular/material/snack-bar";
import {UserService} from "@core/shared/services/user.service";
import {PaymentService} from "@core/shared/services/payment.service";
import {FormService} from "@core/shared/services/form.service";
import {CommissionPaymentService} from "@core/shared/services/commission-payment.service";
import {CommissionService} from "@core/shared/services/commission.service";
import {User} from "@core/shared/models/user";
import {Invoice} from "@core/shared/models/invoice";
import {browserInfos} from "@core/shared/utils/browser-infos";
import {PaymentInitialize} from "@core/shared/models/payment-initialize";
import {Observable, Subject, throwError} from "rxjs";
import {catchError} from "rxjs/operators";
import {validateNumericOnly} from "@core/shared/utils/card-number";
import {parsePrice} from "@core/shared/utils/price";
import {parsePercent} from "@core/shared/utils/percent";
import {Currency} from "@core/shared/models/currency";
import {InvoiceRow} from "@core/shared/models/invoice/invoice-row";

@Component({
    selector: 'app-core-page-commission-payment',
    templateUrl: './page-commission-payment.component.html',
    styleUrl: './page-commission-payment.component.scss',
    providers: [
        FormService
    ],
    standalone: false
})
export class PageCommissionPaymentComponent implements OnInit {

    public loaderPayment$: Subject<boolean> = new Subject<boolean>();

    public cardRegistrationUrl: string;

    public invoice: Invoice;

    public isPaymentDataRequired: boolean = false;

    public paymentFormFieldsInitialized: boolean = false;

    public isSubmitAllowed: boolean = false;

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private _router: Router,
        private _translateService: TranslateService,
        private _snackBar: MatSnackBar,
        private _userService: UserService,
        private _paymentService: PaymentService,
        private _commissionService: CommissionService,
        private _commissionPaymentService: CommissionPaymentService,
        public formService: FormService
    ) {}

    ngOnInit() {

        this._initForm();

        this._loadCommissions();
    }

    private _initForm(): void {

        this.formService.form = this._formBuilder.group({});

        this.formService.submitCallback = (): void => {

            this.loaderPayment$.next(true);

            const dataToValidate: object = {
                browserInfo: browserInfos()
            };

            if (this.isPaymentDataRequired) {

                const dataToCardRegistration = `cardNumber=${this.form.get('cardNumber').value.replace(/\s/g, '')}&cardExpirationDate=${this.form.get('cardExpirationDate').value.replace('/', '')}&cardCvx=${this.form.get('cardCvx').value}&accessKeyRef=${this.form.get('accessKey').value}&data=${this.form.get('data').value}`;

                this._paymentService.cardRegistrationUrl(this.cardRegistrationUrl, dataToCardRegistration).subscribe((data: ArrayBuffer): void => {

                    dataToValidate['cardRegistration'] = {
                        "data": data,
                        "id": this.form.get('id').value
                    };

                    this._validatePayment(dataToValidate);
                });
            } else {

                this._validatePayment(dataToValidate);
            }
        }
    }

    private _loadCommissions(): void {

        this._commissionService.getSocietyItemsInvoiceAPI(this.currentUser.society.id).subscribe((invoice: Invoice): void => {

            this.invoice = invoice;

            this._paymentInitialize();
        });
    }

    private _paymentInitialize(): void {

        this.isSubmitAllowed = false;

        this._commissionPaymentService.paymentInitialize(this.currentUser.society.id).subscribe((paymentInitialize: PaymentInitialize): void => {

            this.isPaymentDataRequired = !!Object.values(paymentInitialize).length;

            if (this.isPaymentDataRequired) {

                this.cardRegistrationUrl = paymentInitialize.url;

                if (!this.paymentFormFieldsInitialized) {

                    this._initPaymentFormFields();
                }

                this.form.get('accessKey').patchValue(paymentInitialize.accessKeyRef);

                this.form.get('data').patchValue(paymentInitialize.data);

                this.form.get('id').patchValue(paymentInitialize.id);
            }

            this.isSubmitAllowed = true;
        });
    }

    private _initPaymentFormFields(): void {

        this.form.addControl('cardNumber', this._formBuilder.control(null, [Validators.required, (control: UntypedFormControl): (object | null) => {

            const field = {...control};

            let cardNumber = field.value;

            cardNumber = (cardNumber) ? cardNumber.replace(/\s/g, "") : "";

            return validateNumericOnly(cardNumber) === false ? {invalidNUmberCard: {valid: false}} : null;
        }]));

        this.form.addControl('cardExpirationDate', this._formBuilder.control(null, [Validators.required, (control: UntypedFormControl): (object | null) => {

            const field = {...control};

            let cardDate = field.value ? field.value.replace("/", "") : "";

            const currentDate = new Date();

            if (cardDate.length === 4) {

                const year = parseInt(cardDate.substr(2, 2), 10) + 2000;

                const month = parseInt(cardDate.substr(0, 2), 10);

                if (month > 0 && month <= 12) {

                    const currentYear = currentDate.getFullYear();

                    if (currentYear < year) {

                        return null;
                    }

                    if (currentYear === year) {

                        const currentMonth = currentDate.getMonth() + 1;

                        if (currentMonth <= month) {

                            return null;
                        }
                    }

                    return {invalidDatePast: {valid: false}};
                }
            }

            return {invalidFormatDate: {valid: false}};
        }]));

        this.form.addControl('cardCvx', this._formBuilder.control(null, [Validators.required, (control: UntypedFormControl): (object | null) => {

            const field = {...control};

            const cvv = field.value ? field.value.trim() : "";

            if (validateNumericOnly(cvv) === true) {

                if (cvv.length === 3) {

                    return null;
                }
            }

            return {invalidFormatCvv: {valid: false}};
        }]));

        this.form.addControl('accessKey', this._formBuilder.control(null, [Validators.required]));

        this.form.addControl('data', this._formBuilder.control(null, [Validators.required]));

        this.form.addControl('id', this._formBuilder.control(null, [Validators.required]));

        this.paymentFormFieldsInitialized = true;
    }

    private _validatePayment(dataToValidate: object): void {

        this._commissionPaymentService.paymentValidate(this.currentUser.society.id, dataToValidate).pipe(catchError((): Observable<never> => {

            this.loaderPayment$.next(false);

            this._paymentInitialize();

            this._snackBar.open(this._translateService.instant('payment.error.card.value'), this._translateService.instant('error.notification.close.value'), {
                duration: 8000
            });

            return throwError(this._translateService.instant('payment.error.card.value'));

        })).subscribe((data: { status: string, redirectUrl: string, paymentId: string }): void => {

            this.loaderPayment$.next(false);

            if (data.status === 'REDIRECT') {

                location.href = data.redirectUrl;
            }

            if (data.status === 'SUCCESS') {

                // Mise à jour de l'utilisateur connecté

                this._userService.refreshUser(this.currentUser.id, (): void => {

                    if (data.paymentId) {

                        this._router.navigate([`account/commission/payment-response/success/${data.paymentId}`]);
                    } else {

                        this._router.navigate([`account/commission/payment-response/success`]);
                    }
                });
            }
        });
    }

    public parsePrice(price: number, currency?: Currency): string {

        return `${parsePrice(price / 100)} ${currency ? currency.symbol : this.invoice.currency.symbol}`;
    }

    public parsePercent(value: number): string {

        return `${parsePercent(value * 100)} %`;
    }

    get currentUser(): User {

        return this._userService.currentUser.getValue();
    }

    get form(): UntypedFormGroup {

        return this.formService.form;
    }

    get rateFieldsDisplayed(): boolean {

        return this.invoice.rows.some((row: InvoiceRow): boolean => {

            return row.initialCurrency.symbol !== this.invoice.currency.symbol;
        });
    }

    get columnCount(): number {

        const staticColumns: string[] = [
            'description',
            'unitPriceHT',
            'vatByPercent',
            'vatByCurrency',
            'priceTTC'
        ];

        if(this.rateFieldsDisplayed){

            staticColumns.push('initialUnitPriceHT', 'exchangeRate');
        }

        return staticColumns.length;
    }
}
